import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn, Icon, Skeleton } from "@nexus/ui";
import type { BigQueryUsageFingerprint } from "@/features/bigquery-usage/api/bigquery-usage.api";
import { SortHeader } from "@/features/identity-hub/components/dashboard/shared/table/SortHeader";
import type { SortState } from "@/features/identity-hub/lib/sort";
import { formatMicrosAsUsd } from "@/features/llm-usage/lib/money";
import { SECONDARY_LIST_MAX_HEIGHT, STICKY_HEAD_CLASS } from "@/features/llm-usage/lib/table-scroll";
import { sortTableRows, toggleUsageSort, type SortValue } from "@/features/admin/lib/table-sort";
import { formatByteShare, formatBytesCompact, formatBytesExact } from "../lib/bytes";
import { formatSqlPreview, shortFingerprint } from "../lib/fingerprint-display";
import { splitSqlNote } from "../lib/sql-note";
import { SqlLine } from "./SqlLine";

interface BigQueryFingerprintTableProps {
  rows: BigQueryUsageFingerprint[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isStale: boolean;
  totalBytesBilled: number;
  selectedFingerprint?: string;
  onSelectFingerprint: (sqlFingerprint: string) => void;
}

const HEADER_BASE =
  "border-b border-border px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground";
const HEADER_CLASS = cn(HEADER_BASE, "w-full");
const NUMERIC_HEADER_CLASS = cn(HEADER_BASE, "text-right");
const CELL_CLASS = "px-3 py-2 text-xs text-foreground";
// See the note in `BigQueryTenantTable`: without this a squeezed numeric column
// breaks a value between its number and its unit.
const NUMERIC_CELL_CLASS = cn(CELL_CLASS, "text-right tabular-nums whitespace-nowrap");
const SKELETON_ROWS = 3;
const COLUMN_COUNT = 5;
const DASH = "—";

type FingerprintSortKey = "statement" | "queries" | "bytesBilled" | "costMicros" | "lastSeenAt";

const DEFAULT_SORT: SortState<FingerprintSortKey> = { key: "bytesBilled", dir: "desc" };
const TEXT_KEYS: ReadonlySet<FingerprintSortKey> = new Set<FingerprintSortKey>(["statement"]);

function fingerprintSortValue(row: BigQueryUsageFingerprint, key: FingerprintSortKey): SortValue {
  // The note is not what the column shows any more, so sorting on the raw
  // preview would order the rows by a prefix that is no longer first.
  if (key === "statement") return splitSqlNote(row.sqlPreview).statement;
  if (key === "lastSeenAt") return Date.parse(row.lastSeenAt);
  return row[key];
}

/**
 * When the statement last ran. A month and a day rather than a full timestamp:
 * the question is whether this template is still in use, and the exact second is
 * on the hover.
 */
function formatLastSeen(lastSeenAt: string, locale: string): string {
  return new Date(lastSeenAt).toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Bytes billed per normalized statement, heaviest first.
 *
 * The preview is the template the fingerprint was taken over, never a truncation
 * of one execution, so two rows that look alike really are two different
 * statements — which is why the hash and the fingerprint version are both on the
 * row. A version bump re-hashes the same SQL, so rows from two versions are not
 * comparable and the table has to say which one it is showing.
 */
export function BigQueryFingerprintTable({
  rows,
  isLoading,
  isError,
  onRetry,
  isStale,
  totalBytesBilled,
  selectedFingerprint,
  onSelectFingerprint,
}: BigQueryFingerprintTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [sort, setSort] = useState<SortState<FingerprintSortKey>>(DEFAULT_SORT);

  const sortedRows = useMemo(() => sortTableRows(rows, sort, fingerprintSortValue), [rows, sort]);
  const handleSort = (key: FingerprintSortKey) =>
    setSort((prev) => toggleUsageSort(prev, key, TEXT_KEYS));

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-md border border-border bg-card">
      <div className="border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("admin.bigQueryUsage.fingerprintTable.title", "By statement")}
      </div>
      <div className={cn(SECONDARY_LIST_MAX_HEIGHT, "overflow-y-auto")}>
        <table className="w-full border-collapse">
          <caption className="sr-only">
            {t("admin.bigQueryUsage.fingerprintTable.caption", "BigQuery usage per statement")}
          </caption>
          <thead className={STICKY_HEAD_CLASS}>
            <tr className="group">
              <SortHeader
                id="statement"
                label={t("admin.bigQueryUsage.fingerprintTable.statement", "Statement")}
                sort={sort}
                onSort={handleSort}
                className={HEADER_CLASS}
              />
              <SortHeader
                id="queries"
                label={t("admin.bigQueryUsage.table.queries", "Queries")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="bytesBilled"
                label={t("admin.bigQueryUsage.table.bytesBilled", "Bytes billed")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="costMicros"
                label={t("admin.bigQueryUsage.table.cost", "Cost")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="lastSeenAt"
                label={t("admin.bigQueryUsage.fingerprintTable.lastSeen", "Last seen")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
            </tr>
          </thead>
          <tbody
            className={cn("transition-opacity duration-200", isStale && "opacity-40")}
            aria-busy={isStale || undefined}
          >
            {isError ? (
              <tr>
                <td className={cn(CELL_CLASS, "py-6 text-center")} colSpan={COLUMN_COUNT}>
                  <span className="flex flex-col items-center gap-2 text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Icon name="alert-circle" size="xs" colorVar="--destructive" aria-hidden />
                      {t(
                        "admin.bigQueryUsage.fingerprintTable.error",
                        "Could not load the statement breakdown.",
                      )}
                    </span>
                    <Button variant="outline" size="sm" onClick={onRetry}>
                      {t("admin.bigQueryUsage.retry", "Try again")}
                    </Button>
                  </span>
                </td>
              </tr>
            ) : isLoading && rows.length === 0 ? (
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={index} className="border-b border-border last:border-0">
                  <td className={CELL_CLASS} colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-4 w-full rounded-sm bg-foreground/8" />
                  </td>
                </tr>
              ))
            ) : sortedRows.length === 0 ? (
              <tr>
                <td
                  className={cn(CELL_CLASS, "py-6 text-center text-muted-foreground")}
                  colSpan={COLUMN_COUNT}
                >
                  {t("admin.bigQueryUsage.table.empty", "No BigQuery queries in this window")}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const share = formatByteShare(row.bytesBilled, totalBytesBilled, locale);
                const isSelected = row.sqlFingerprint === selectedFingerprint;
                const shortHash = shortFingerprint(row.sqlFingerprint);
                const { note, statement } = splitSqlNote(row.sqlPreview);

                return (
                  <tr
                    key={row.sqlFingerprint}
                    onClick={() => onSelectFingerprint(row.sqlFingerprint)}
                    className={cn(
                      "cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40",
                      isSelected && "bg-primary/5 hover:bg-primary/5",
                    )}
                  >
                    <td className={cn(CELL_CLASS, "max-w-0")}>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={
                          isSelected
                            ? t(
                                "admin.bigQueryUsage.fingerprintTable.clearFilter",
                                "Stop filtering by the statement {{fingerprint}}",
                                { fingerprint: shortHash },
                              )
                            : t(
                                "admin.bigQueryUsage.fingerprintTable.filterBy",
                                "Show only the statement {{fingerprint}}",
                                { fingerprint: shortHash },
                              )
                        }
                        className="flex w-full max-w-full flex-col items-start rounded-sm text-left outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        {statement.length === 0 ? (
                          <span
                            className="max-w-full truncate font-mono text-[11px] text-muted-foreground"
                            title={row.sqlPreview}
                          >
                            {formatSqlPreview(note ?? "")}
                          </span>
                        ) : (
                          <SqlLine
                            line={formatSqlPreview(statement)}
                            className="max-w-full truncate font-mono text-[11px]"
                            title={row.sqlPreview}
                          />
                        )}
                        <span className="flex w-full items-center gap-1.5 text-[11px] text-muted-foreground">
                          <span className="font-mono">{shortHash}</span>
                          <span
                            title={t(
                              "admin.bigQueryUsage.fingerprintTable.versionTitle",
                              "Fingerprint version {{version}}. Rows hashed by different versions are not comparable.",
                              { version: row.fingerprintVersion },
                            )}
                          >
                            {t("admin.bigQueryUsage.fingerprintTable.version", "v{{version}}", {
                              version: row.fingerprintVersion,
                            })}
                          </span>
                          {/* The note is how the data team labels a builder —
                              `nexus:data-sync:personas` names one — so it stays
                              on the row, behind the statement it used to hide.
                              It keeps the `--` the collapse left it without, so
                              it reads as a comment rather than as more metadata. */}
                          {note === null || statement.length === 0 ? null : (
                            <span className="min-w-0 truncate font-mono">{`-- ${note}`}</span>
                          )}
                        </span>
                      </button>
                    </td>
                    <td className={NUMERIC_CELL_CLASS}>{row.queries.toLocaleString(locale)}</td>
                    <td
                      className={cn(NUMERIC_CELL_CLASS, "font-medium")}
                      title={formatBytesExact(row.bytesBilled, locale)}
                    >
                      <span className="flex items-baseline justify-end gap-1.5">
                        <span>{formatBytesCompact(row.bytesBilled, locale)}</span>
                        {share ? (
                          <span className="text-[11px] font-normal text-muted-foreground">
                            {share}
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td
                      className={cn(
                        NUMERIC_CELL_CLASS,
                        row.costMicros == null && "text-muted-foreground",
                      )}
                      title={
                        row.costMicros == null
                          ? t(
                              "admin.bigQueryUsage.table.costUnpricedTitle",
                              "No price per TB is set. Set it in Admin → Settings → Operations.",
                            )
                          : undefined
                      }
                    >
                      {row.costMicros == null ? DASH : formatMicrosAsUsd(row.costMicros, locale)}
                    </td>
                    <td className={cn(NUMERIC_CELL_CLASS, "text-muted-foreground")}>
                      {formatLastSeen(row.lastSeenAt, locale)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
